import { useContext, useEffect, useState } from 'react';
import {
    Grid,
    Box,
    InputLabel,
    FormControl,
    Select,
    MenuItem,
    Typography,
    Card,
    CardContent,
    SelectChangeEvent,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
    AppContext,
    SttSportSelector,
    SttSelectInterval,
    SttFormText,
    SttFormTextarea,
    SttButtonSave,
    SttButtonDelete,
    SttButtonCancel,
    SttButtonRecover,
    SttFormTime,
    SttFullDialog,
    SttFormCheck,
} from '../../../sporttia/all';
import { SttTerrainList } from '../../../sporttia/lists/SttTerrainList';
import { SttSelectNumbersInterval } from '../../../sporttia/selectors/SttSelectNumbersInterval';
import useFacilitiesService from '../../../services/FacilitiesService';
import type { Sport } from '../../../types/models/Sport';
import type { Terrain } from '../../../types/models/Terrain';
import {
    FacilityParams,
    FacilityResponse,
} from '../../../types/api/paths/Facility';
import type { Facility } from '../../../types/models/Facility';
import { getErrorMessage } from '../../../lib/utils';
import constants from '../../../config/constants';
import type { Error } from '../../../types/models/Error';
import { PERMISSIONS } from '../../../lib/permissions';

type FacilityParam = {
    id: number;
    trash: boolean;
};

type FacilityProps = {
    item: FacilityParam;
    onClose: () => void;
    onNotFound?: () => void;
};

type FormChangeEvent = {
    name?: string;
    value: unknown;
};

type TerrainResponse = {
    terrain: Terrain;
};

export default function FacilityView({
    item,
    onClose,
    onNotFound,
}: FacilityProps) {
    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();
    const [facility, setFacility] = useState<Facility>();
    const [loading, setLoading] = useState(false);
    const facilitiesService = useFacilitiesService();

    function loadFacility(trashOverride?: boolean) {
        const trash = trashOverride ?? item.trash;
        facilitiesService
            .get<FacilityResponse>(item.id, { trash })
            .then((response: FacilityResponse) => {
                if (response.facility) {
                    setFacility({ ...response.facility });
                }
            })
            .catch((error: unknown) => {
                const status = (error as { error?: { status?: number } })?.error
                    ?.status;
                if (status === 404 && onNotFound) {
                    onNotFound();
                    return;
                }
                cxt.showMessage('E', getErrorMessage(error));
            });
    }

    /**
     * Init
     */
    useEffect(() => {
        // Creating new facility
        if (typeof item.id === 'number') {
            if (item.id === 0) {
                setFacility({
                    defaultBooking: 0,
                    id: 0,
                    maxBooking: 0,
                    timeiniLightsOn: '',
                    name: '',
                    description: '',
                    address: '',
                    status: '',
                    position: null,
                    mobile: '',
                    cancelation: null,
                    bookingInfo: '',
                    sport: null,
                    terrains: [],
                    sets: [],
                    maxOccupants: 0,
                    generateOccupantUnlocker: 'NULL',
                    isForGame: false,
                    schedules: [],
                    gameCancelation: 0,
                });

                cxt.setHeader({
                    title: t('NewFacility'),
                });

                // Updating facility
            } else {
                loadFacility();
            }
        }
    }, [item.id, item.trash]);

    /**
     * Update facility
     * Dependiendo de si estamos creando nueva o modificando, llamamos a distintas funciones
     */
    function save() {
        if (!facility) {
            return;
        }

        // CU-869egchpe: se mantiene el invariante "lo que no se puede marcar en
        // pantalla no se envía como activo" (mismo predicado que gobierna el
        // render de la casilla). Así, abrir y guardar una instalación que quedó
        // con la configuración de partidos huérfana la deja consistente.
        const sportAllowsGames = !!facility.sport?.isForGame;

        const params: FacilityParams = {
            name: facility.name,
            description: facility.description,
            address: facility.address,
            status: facility.status,
            position: facility.position,
            mobile: facility.mobile,
            cancelation: facility.cancelation ?? 0,
            bookingInfo: facility.bookingInfo,
            defaultBooking: facility.defaultBooking,
            maxBooking: facility.maxBooking,
            timeiniLightsOn: facility.timeiniLightsOn,
            maxOccupants: facility.maxOccupants,
            idSport: facility?.sport?.id || null,
            generateOccupantUnlocker:
                facility?.generateOccupantUnlocker ===
                constants.unlocker.types.noUnlocker
                    ? null
                    : facility?.generateOccupantUnlocker,
            isForGame: sportAllowsGames && facility.isForGame,
            gameCancelation: sportAllowsGames
                ? (facility.gameCancelation ?? 0)
                : 0,
        };

        setLoading(true);
        if (facility.id) {
            facilitiesService
                .update<FacilityResponse>(facility.id, params)
                .then(() => {
                    cxt.showMessage('S', t('Modified'));
                })
                .catch((errorWrapper: Error) => {
                    cxt.showMessage('E', t(errorWrapper.error.msg));
                })
                .finally(() => setLoading(false));
        } else {
            facilitiesService
                .create(cxt?.sc?.id, params)
                .then((response: FacilityResponse) => {
                    if (response.facility) {
                        setFacility(response.facility);
                        cxt.showMessage('S', t('Created'));
                    }
                })
                .catch((errorWrapper: Error) => {
                    cxt.showMessage('E', t(errorWrapper.error.msg));
                })
                .finally(() => setLoading(false));
        }
    }

    /**
     * Delete facility
     */
    function deleteFacility() {
        if (facility && facility.id) {
            facilitiesService
                .remove(facility.id)
                .then(onClose)
                .catch(() => {});
        }
    }

    function recoverFacility() {
        if (facility && facility.id) {
            facilitiesService
                .recover<FacilityResponse>(facility.id)
                .then(() => {
                    loadFacility(false);
                });
        }
    }

    // Añadir un nuevo terreno mediante API
    function addNewTerrain() {
        if (facility && facility.id) {
            facilitiesService
                .createTerrain(facility?.id)
                .then((response: TerrainResponse) => {
                    if (response.terrain) {
                        const newTerrain = {
                            id: response.terrain.id,
                            name: response.terrain.name,
                        };
                        setFacility({
                            ...facility,
                            terrains: facility.terrains
                                ? facility.terrains.concat([newTerrain])
                                : [newTerrain],
                        });
                    }
                });
        }
    }

    // Añadir terreno existente mediante API
    function addExistingTerrain(id: number) {
        if (facility && facility.id) {
            facilitiesService
                .addExistingTerrain(facility?.id, id)
                .then((response: TerrainResponse) => {
                    if (response.terrain) {
                        const newTerrain = {
                            id: response.terrain.id,
                            name: response.terrain.name,
                        };
                        setFacility({
                            ...facility,
                            terrains: facility.terrains
                                ? facility.terrains.concat([newTerrain])
                                : [newTerrain],
                        });
                    }
                });
        }
    }

    // Eliminar terreno mediante API
    function removeTerrain(terrain: Terrain) {
        if (facility && facility.id) {
            facilitiesService
                .removeTerrain(facility?.id, terrain.id)
                .then(() => {
                    setFacility({
                        ...facility,
                        terrains: facility.terrains.filter(
                            (trn) => trn.id !== terrain.id,
                        ),
                    });
                });
        }
    }

    if (!facility) {
        return null;
    }

    return (
        <SttFullDialog
            open={item.id !== null}
            onClose={onClose}
            title={item.id === 0 ? t('CreateFacility') : facility.name}
            urlPath={item.id ? `/facilities/${item.id}` : undefined}
        >
            <Box className="p-6">
                <Grid container spacing={3}>
                    {/* Top Left - Basic Info */}
                    <Grid size={{ xs: 12, lg: 6 }}>
                        <Card variant="outlined" className="h-full">
                            <CardContent>
                                <Typography
                                    variant="subtitle1"
                                    fontWeight={600}
                                    className="pb-2 border-b border-gray-200"
                                >
                                    {t('BasicInfo')}
                                </Typography>

                                <Grid container spacing={2} className="mt-4">
                                    <Grid size={{ xs: 12 }}>
                                        <SttFormText
                                            name="name"
                                            caption={t('Name')}
                                            defVal={facility.name}
                                            onChange={({
                                                value,
                                            }: FormChangeEvent) => {
                                                setFacility({
                                                    ...facility,
                                                    name: value as string,
                                                });
                                            }}
                                        />
                                    </Grid>

                                    <Grid size={{ xs: 12, sm: 6 }}>
                                        <SttSportSelector
                                            fullWidth
                                            defVal={
                                                facility?.sport?.id
                                                    ? t(
                                                          `sports.${facility.sport.id}`,
                                                          {
                                                              defaultValue:
                                                                  facility.sport
                                                                      .name,
                                                          },
                                                      )
                                                    : ''
                                            }
                                            onChange={(sport) => {
                                                // CU-869egchpe: si el deporte
                                                // nuevo no admite partidos, se
                                                // limpia su configuración. De
                                                // lo contrario quedaría oculta
                                                // en pantalla pero viva en el
                                                // estado, y se guardaría.
                                                const selectedSport =
                                                    sport as Sport;
                                                const sportAllowsGames =
                                                    !!selectedSport?.isForGame;

                                                setFacility({
                                                    ...facility,
                                                    sport: selectedSport,
                                                    isForGame:
                                                        sportAllowsGames &&
                                                        facility.isForGame,
                                                    gameCancelation:
                                                        sportAllowsGames
                                                            ? facility.gameCancelation
                                                            : 0,
                                                });
                                            }}
                                        />
                                    </Grid>

                                    <Grid size={{ xs: 12, sm: 6 }}>
                                        <FormControl
                                            variant="outlined"
                                            size="small"
                                            fullWidth
                                        >
                                            <InputLabel>
                                                {t('Status')}
                                            </InputLabel>
                                            <Select
                                                value={facility.status}
                                                onChange={(
                                                    e: SelectChangeEvent<string>,
                                                ) =>
                                                    setFacility({
                                                        ...facility,
                                                        status: String(
                                                            e.target.value,
                                                        ),
                                                    })
                                                }
                                                label={t('Status')}
                                            >
                                                <MenuItem value="ACTIVE">
                                                    {t('FS.ACTIVE')}
                                                </MenuItem>
                                                <MenuItem value="ONLY_SC">
                                                    {t(
                                                        'const.FS.ACTIVE_ONLY_SC',
                                                    )}
                                                </MenuItem>
                                                <MenuItem value="INACTIVE">
                                                    {t('FS.INACTIVE')}
                                                </MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Grid>

                                    <Grid size={{ xs: 12 }}>
                                        <SttFormTextarea
                                            name="description"
                                            caption={t('Description')}
                                            defVal={facility.description}
                                            onChange={({
                                                value,
                                            }: FormChangeEvent) => {
                                                setFacility({
                                                    ...facility,
                                                    description:
                                                        value as string,
                                                });
                                            }}
                                        />
                                    </Grid>

                                    <Grid size={{ xs: 12, sm: 8 }}>
                                        <SttFormText
                                            name="address"
                                            caption={t('Address')}
                                            defVal={facility.address}
                                            onChange={({
                                                value,
                                            }: FormChangeEvent) => {
                                                setFacility({
                                                    ...facility,
                                                    address: value as string,
                                                });
                                            }}
                                        />
                                    </Grid>

                                    <Grid size={{ xs: 12, sm: 4 }}>
                                        <SttFormText
                                            name="mobile"
                                            caption={t('Mobile')}
                                            defVal={facility.mobile}
                                            onChange={({
                                                value,
                                            }: FormChangeEvent) => {
                                                setFacility({
                                                    ...facility,
                                                    mobile: value as string,
                                                });
                                            }}
                                        />
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Top Right - Booking Settings */}
                    <Grid size={{ xs: 12, lg: 6 }}>
                        <Card variant="outlined" className="h-full">
                            <CardContent>
                                <Typography
                                    variant="subtitle1"
                                    fontWeight={600}
                                    className="pb-2 border-b border-gray-200"
                                >
                                    {t('BookingSettings')}
                                </Typography>

                                <Grid container spacing={2} className="mt-4">
                                    <Grid size={{ xs: 12, sm: 6 }}>
                                        <SttSelectInterval
                                            caption={t('DefaultBookingPeriod')}
                                            defVal={facility.defaultBooking}
                                            min={15}
                                            max={600}
                                            interval={15}
                                            onChange={(value: number) =>
                                                setFacility({
                                                    ...facility,
                                                    defaultBooking: value,
                                                })
                                            }
                                        />
                                    </Grid>

                                    <Grid size={{ xs: 12, sm: 6 }}>
                                        <SttSelectInterval
                                            caption={t('MaxBookingPeriod')}
                                            defVal={facility.maxBooking}
                                            min={15}
                                            max={600}
                                            interval={15}
                                            onChange={(value: number) => {
                                                setFacility({
                                                    ...facility,
                                                    maxBooking: value,
                                                });
                                            }}
                                        />
                                    </Grid>

                                    <Grid size={{ xs: 12, sm: 6 }}>
                                        <SttFormText
                                            name="cancelation"
                                            caption={`${t(
                                                'Cancelation',
                                            )} (${t('hours')})`}
                                            defVal={
                                                facility?.cancelation?.toString() ||
                                                ''
                                            }
                                            onChange={({
                                                value,
                                            }: FormChangeEvent) => {
                                                const strValue =
                                                    value as string;
                                                setFacility({
                                                    ...facility,
                                                    cancelation: Number.isNaN(
                                                        parseInt(strValue),
                                                    )
                                                        ? null
                                                        : parseInt(strValue),
                                                });
                                            }}
                                        />
                                    </Grid>

                                    <Grid size={{ xs: 12, sm: 6 }}>
                                        <SttSelectNumbersInterval
                                            caption={t('occupants.max')}
                                            defVal={facility.maxOccupants}
                                            min={0}
                                            max={50}
                                            interval={1}
                                            onChange={(value: number) => {
                                                setFacility({
                                                    ...facility,
                                                    maxOccupants: value,
                                                });
                                            }}
                                        />
                                    </Grid>

                                    <Grid size={{ xs: 12 }}>
                                        <SttFormTextarea
                                            name="bookingInfo"
                                            caption={t(
                                                'page.sc.facilities.infoRent',
                                            )}
                                            defVal={facility.bookingInfo}
                                            onChange={({
                                                value,
                                            }: FormChangeEvent) => {
                                                setFacility({
                                                    ...facility,
                                                    bookingInfo:
                                                        value as string,
                                                });
                                            }}
                                        />
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Bottom Left - Advanced Settings */}
                    <Grid size={{ xs: 12, lg: 6 }}>
                        <Card variant="outlined" className="h-full">
                            <CardContent>
                                <Typography
                                    variant="subtitle1"
                                    fontWeight={600}
                                    className="pb-2 border-b border-gray-200"
                                >
                                    {t('AdvancedSettings')}
                                </Typography>

                                <Grid container spacing={2} className="mt-4">
                                    {cxt.zero && (
                                        <Grid size={{ xs: 12, sm: 6 }}>
                                            <SttFormText
                                                name="position"
                                                caption={t('Position')}
                                                defVal={
                                                    facility?.position?.toString() ||
                                                    ''
                                                }
                                                onChange={({
                                                    value,
                                                }: FormChangeEvent) => {
                                                    setFacility({
                                                        ...facility,
                                                        position: parseInt(
                                                            value as string,
                                                            10,
                                                        ),
                                                    });
                                                }}
                                            />
                                        </Grid>
                                    )}

                                    {!cxt.zero && (
                                        <>
                                            <Grid size={{ xs: 12, sm: 6 }}>
                                                <SttFormText
                                                    name="position"
                                                    caption={t('Position')}
                                                    defVal={
                                                        facility?.position?.toString() ||
                                                        ''
                                                    }
                                                    onChange={({
                                                        value,
                                                    }: FormChangeEvent) => {
                                                        setFacility({
                                                            ...facility,
                                                            position: parseInt(
                                                                value as string,
                                                                10,
                                                            ),
                                                        });
                                                    }}
                                                />
                                            </Grid>

                                            <Grid size={{ xs: 12, sm: 6 }}>
                                                <SttFormTime
                                                    interval={5}
                                                    caption={t(
                                                        'TimeIniLightsOn',
                                                    )}
                                                    name="timeiniLightsOn"
                                                    max="24:00:00"
                                                    min="00:00:00"
                                                    defVal={
                                                        facility.timeiniLightsOn
                                                    }
                                                    onChange={({
                                                        value,
                                                    }: {
                                                        value: string;
                                                    }) => {
                                                        setFacility({
                                                            ...facility,
                                                            timeiniLightsOn:
                                                                value,
                                                        });
                                                    }}
                                                    disableClearable={false}
                                                />
                                            </Grid>

                                            <Grid size={{ xs: 12, sm: 6 }}>
                                                <FormControl
                                                    variant="outlined"
                                                    size="small"
                                                    fullWidth
                                                >
                                                    <InputLabel>
                                                        {t(
                                                            'generic.generateUnlockers',
                                                        )}
                                                    </InputLabel>
                                                    <Select
                                                        value={
                                                            facility.generateOccupantUnlocker ||
                                                            constants.unlocker
                                                                .types
                                                                .noUnlocker
                                                        }
                                                        onChange={(
                                                            e: SelectChangeEvent<string>,
                                                        ) => {
                                                            const value =
                                                                String(
                                                                    e.target
                                                                        .value,
                                                                );
                                                            setFacility({
                                                                ...facility,
                                                                generateOccupantUnlocker:
                                                                    value ===
                                                                    t(
                                                                        'generic.noUnlockers',
                                                                    )
                                                                        ? null
                                                                        : value,
                                                            });
                                                        }}
                                                        label={t(
                                                            'generic.generateUnlockers',
                                                        )}
                                                    >
                                                        <MenuItem
                                                            value={
                                                                constants
                                                                    .unlocker
                                                                    .types
                                                                    .noUnlocker
                                                            }
                                                        >
                                                            {t(
                                                                'generic.noUnlockers',
                                                            )}
                                                        </MenuItem>
                                                        <MenuItem
                                                            value={
                                                                constants
                                                                    .unlocker
                                                                    .types.QR
                                                            }
                                                        >
                                                            {t('unlockers.QR')}
                                                        </MenuItem>
                                                    </Select>
                                                </FormControl>
                                            </Grid>

                                            {facility.sport?.isForGame && (
                                                <Grid size={{ xs: 12, sm: 6 }}>
                                                    <SttFormCheck
                                                        name="isForGame"
                                                        caption={t(
                                                            'FacilityForGame',
                                                        )}
                                                        checked={
                                                            !!facility.isForGame
                                                        }
                                                        onChange={({
                                                            value,
                                                        }: FormChangeEvent) =>
                                                            setFacility({
                                                                ...facility,
                                                                isForGame:
                                                                    value as boolean,
                                                            })
                                                        }
                                                    />
                                                </Grid>
                                            )}

                                            {facility.isForGame && (
                                                <Grid size={{ xs: 12, sm: 6 }}>
                                                    <SttFormText
                                                        name="gameCancelation"
                                                        caption={`${t(
                                                            'automaticGameCancelation',
                                                        )} (${t('hours')})`}
                                                        defVal={
                                                            facility?.gameCancelation?.toString() ||
                                                            ''
                                                        }
                                                        onChange={({
                                                            value,
                                                        }: FormChangeEvent) => {
                                                            const strValue =
                                                                value as string;
                                                            setFacility({
                                                                ...facility,
                                                                gameCancelation:
                                                                    Number.isNaN(
                                                                        parseInt(
                                                                            strValue,
                                                                        ),
                                                                    )
                                                                        ? null
                                                                        : parseInt(
                                                                              strValue,
                                                                          ),
                                                            });
                                                        }}
                                                    />
                                                </Grid>
                                            )}
                                        </>
                                    )}
                                </Grid>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Bottom Right - Terrains */}
                    {facility.id !== null && facility.id !== 0 && (
                        <Grid size={{ xs: 12, lg: 6 }}>
                            <Card variant="outlined" className="h-full">
                                <CardContent>
                                    <SttTerrainList
                                        terrains={facility.terrains || []}
                                        onCreate={() => addNewTerrain()}
                                        onAdd={(terrain: Terrain) => {
                                            addExistingTerrain(terrain.id);
                                        }}
                                        onDelete={(terrain: Terrain) =>
                                            removeTerrain(terrain)
                                        }
                                    />
                                </CardContent>
                            </Card>
                        </Grid>
                    )}
                </Grid>

                {/* Action Buttons */}
                <Box className="mt-6 flex flex-wrap justify-start gap-4">
                    {facility.trash && (
                        <SttButtonRecover
                            onClick={recoverFacility}
                            permission={PERMISSIONS.FACILITY.DELETE}
                        />
                    )}

                    {typeof facility.id === 'number' &&
                        facility.id > 0 &&
                        !facility.trash && (
                            <SttButtonDelete
                                confirmation
                                onClick={deleteFacility}
                                permission={PERMISSIONS.FACILITY.DELETE}
                            />
                        )}

                    {!facility.trash && (
                        <SttButtonSave
                            loading={loading}
                            onClick={save}
                            permission={
                                facility.id
                                    ? PERMISSIONS.FACILITY.MODIFY
                                    : PERMISSIONS.FACILITY.CREATE
                            }
                        />
                    )}

                    {facility.id === null && (
                        <SttButtonCancel onClick={() => onClose()} />
                    )}
                </Box>
            </Box>
        </SttFullDialog>
    );
}
